import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MentionItem, MentionKind, matchMentionQuery } from './mentionVocab';
import { useTranslation } from 'react-i18next';

const SECTION_ORDER: MentionKind[] = ['agent', 'field', 'tool', 'role'];
const SECTION_LABELS: Record<MentionKind, string> = { agent: 'Agents', field: 'Field classes', tool: 'Tools', role: 'Roles' };
const MAX_PER_SECTION = 8;
// Copied onto the mirror div so its text wraps exactly where the real textarea's does.
const MIRROR_PROPS: Array<keyof CSSStyleDeclaration> = [
  'fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'letterSpacing', 'lineHeight',
  'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
  'borderTopWidth', 'borderRightWidth', 'borderBottomWidth', 'borderLeftWidth', 'boxSizing', 'width',
];

interface MentionPopupProps {
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  value: string;
  vocab: MentionItem[];
  onInsert: (nextValue: string) => void;
  disabled?: boolean;
  maxChars?: number;
}

/**
 * Caret-anchored "@"-mention popup for the Describe box. Fully controlled by `value` — it never
 * owns the textarea's text, only reads it and asks the parent (via onInsert) to replace it, then
 * restores focus/caret itself.
 *
 * DOM listeners are attached to the textarea once (not re-subscribed per render) and read all
 * mutable state through `live` (a ref kept in sync every render) — attaching/detaching listeners
 * on every keystroke would either miss the keystroke that just typed "@" (stale closure over an
 * old `value`) or thrash addEventListener churn; a ref sidesteps both.
 */
export default function MentionPopup({ textareaRef, value, vocab, onInsert, disabled, maxChars }: MentionPopupProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [matchStart, setMatchStart] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const [anchor, setAnchor] = useState<{ top: number; left: number } | null>(null);
  const mirrorRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const pendingCaretRef = useRef<number | null>(null);
  // The value evaluate() last matched against — used to detect an external rewrite of the
  // textarea (e.g. an EXAMPLES button calling setNl() directly, bypassing the textarea's own
  // input events entirely) so a stale popup doesn't linger over unrelated new text.
  const lastEvaluatedValueRef = useRef<string>(value);

  const filtered = useMemo(() => {
    if (!query) return vocab;
    const q = query.toLowerCase();
    return vocab.filter((v) => v.token.toLowerCase().includes(q) || v.label.toLowerCase().includes(q));
  }, [vocab, query]);
  const grouped = useMemo(() => {
    const g: Partial<Record<MentionKind, MentionItem[]>> = {};
    for (const item of filtered) (g[item.kind] ??= []).push(item);
    return g;
  }, [filtered]);
  const flat = useMemo(() => SECTION_ORDER.flatMap((k) => (grouped[k] || []).slice(0, MAX_PER_SECTION)), [grouped]);

  useEffect(() => { setActiveIndex(0); }, [query, open]);

  // Measures where the caret sits in pixels via a hidden mirror of the textarea's text/layout.
  // Only called at popup-open and on scroll/resize/caret-move while open — NOT on every
  // keystroke, since typing more of the query filters the list without moving the caret line.
  const recomputeAnchor = (curValue: string, curMatchStart: number) => {
    const ta = textareaRef.current; const mirror = mirrorRef.current;
    if (!ta || !mirror) return;
    const style = getComputedStyle(ta);
    for (const p of MIRROR_PROPS) (mirror.style as any)[p] = style[p];
    mirror.style.width = `${ta.clientWidth}px`;
    mirror.textContent = curValue.slice(0, curMatchStart);
    const span = document.createElement('span');
    span.textContent = curValue.slice(curMatchStart, ta.selectionStart) || '​';
    mirror.appendChild(span);
    const taRect = ta.getBoundingClientRect();
    const lineHeight = parseFloat(style.lineHeight) || parseFloat(style.fontSize) * 1.2;
    const caretTop = taRect.top + span.offsetTop - ta.scrollTop;
    const caretLeft = taRect.left + span.offsetLeft - ta.scrollLeft;
    // Clamp against the popup's own CSS bounds (a2a.css: max-width 360px, max-height 320px) so it
    // never renders past the viewport edge — the caret can sit anywhere in a full-width textarea,
    // including its rightmost column, where an unclamped left would push the popup off-screen.
    const margin = 8;
    let left = Math.min(caretLeft, window.innerWidth - 360 - margin);
    left = Math.max(margin, left);
    let top = caretTop + lineHeight;
    if (top + 320 > window.innerHeight - margin) top = caretTop - 320 - 4; // flip above the caret line
    top = Math.max(margin, top);
    setAnchor({ top, left });
  };

  const applyMention = (item: MentionItem, curValue: string, curMatchStart: number, curQuery: string) => {
    const before = curValue.slice(0, curMatchStart);
    const after = curValue.slice(curMatchStart + 1 + curQuery.length);
    const inserted = `@${item.token} `;
    const next = before + inserted + after;
    if (maxChars && next.length > maxChars) { setOpen(false); return; }
    pendingCaretRef.current = before.length + inserted.length;
    lastEvaluatedValueRef.current = next; // our own change, not an external rewrite — see the [value] effect above
    onInsert(next);
    setOpen(false);
  };

  // Keeps every value the DOM listeners need current, without re-subscribing them each render.
  const live = useRef({ value, disabled, open, matchStart, query, flat, activeIndex, applyMention });
  useEffect(() => { live.current = { value, disabled, open, matchStart, query, flat, activeIndex, applyMention }; });

  // Restores focus/caret after the parent's setNl(next) re-renders `value` — must run after the
  // prop actually changes (a ref-stored target, applied in a layout effect keyed on `value`).
  useLayoutEffect(() => {
    if (pendingCaretRef.current == null) return;
    const ta = textareaRef.current; const caret = pendingCaretRef.current;
    pendingCaretRef.current = null;
    if (!ta) return;
    ta.focus(); ta.setSelectionRange(caret, caret);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // If `value` changed to something evaluate() never saw (an external rewrite, not a keystroke or
  // our own insertion — both of those already updated lastEvaluatedValueRef before this runs),
  // the popup's matchStart/query refer to text that may no longer exist. Close rather than risk
  // an insertion that mangles unrelated text.
  useEffect(() => {
    if (open && value !== lastEvaluatedValueRef.current) setOpen(false);
    lastEvaluatedValueRef.current = value;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Attached once per textarea mount; reads everything mutable through `live.current`.
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    // Reads `ta.value` (the live DOM value), not `live.current.value` (the React `value` prop):
    // this listener is attached directly on the textarea, so it fires in the target phase before
    // React's own onChange (attached to an ancestor) has processed the same keystroke and updated
    // state — `live.current.value` would still be one character behind at this point.
    const evaluate = () => {
      // Marks this value as "seen through the textarea's own events" before React's [value] effect
      // (above) runs for the same change, so ordinary typing is never mistaken for an external rewrite.
      lastEvaluatedValueRef.current = ta.value;
      const l = live.current;
      if (l.disabled) { setOpen(false); return; }
      const m = matchMentionQuery(ta.value, ta.selectionStart);
      if (!m) { setOpen(false); return; }
      const reanchor = !l.open || m.start !== l.matchStart;
      setQuery(m.query); setMatchStart(m.start); setOpen(true);
      if (reanchor) requestAnimationFrame(() => recomputeAnchor(ta.value, m.start));
    };
    const onKeyDown = (e: KeyboardEvent) => {
      const l = live.current;
      if (!l.open) return;
      // stopPropagation on every key the popup handles — including Escape — so it doesn't also
      // reach the wizard Modal's own document-level Escape-closes-the-modal listener (ui.tsx):
      // dismissing the mention popup must not also discard the user's in-progress draft.
      if (e.key === 'ArrowDown') { e.preventDefault(); e.stopPropagation(); setActiveIndex((i) => (i + 1) % Math.max(l.flat.length, 1)); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); e.stopPropagation(); setActiveIndex((i) => (i - 1 + Math.max(l.flat.length, 1)) % Math.max(l.flat.length, 1)); }
      else if (e.key === 'Enter') { const item = l.flat[l.activeIndex]; if (item) { e.preventDefault(); e.stopPropagation(); l.applyMention(item, ta.value, l.matchStart, l.query); } }
      else if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); }
    };
    const onBlur = () => setOpen(false);
    ta.addEventListener('input', evaluate);
    ta.addEventListener('click', evaluate);
    ta.addEventListener('keyup', evaluate);
    ta.addEventListener('keydown', onKeyDown);
    ta.addEventListener('blur', onBlur);
    return () => {
      ta.removeEventListener('input', evaluate);
      ta.removeEventListener('click', evaluate);
      ta.removeEventListener('keyup', evaluate);
      ta.removeEventListener('keydown', onKeyDown);
      ta.removeEventListener('blur', onBlur);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textareaRef.current]);

  // While open, track scroll/resize so the anchor doesn't drift from the caret.
  useEffect(() => {
    if (!open) return;
    const ta = textareaRef.current;
    if (!ta) return;
    const onScroll = () => recomputeAnchor(live.current.value, live.current.matchStart);
    const onResize = () => recomputeAnchor(live.current.value, live.current.matchStart);
    ta.addEventListener('scroll', onScroll);
    window.addEventListener('resize', onResize);
    return () => { ta.removeEventListener('scroll', onScroll); window.removeEventListener('resize', onResize); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const mirror = <div ref={mirrorRef} aria-hidden style={{ position: 'fixed', top: -9999, left: -9999, visibility: 'hidden', whiteSpace: 'pre-wrap', wordWrap: 'break-word', pointerEvents: 'none' }} />;
  if (!open) return mirror;

  let flatIdx = -1;
  // Portaled straight to document.body: rendered inline, `position: fixed` would be relative to
  // whatever ancestor happens to be the fixed-position containing block (any transform/filter/
  // will-change anywhere up the tree — including the wizard modal's own entrance animation —
  // hijacks it), not the viewport. That made the popup drift further off-screen with every
  // subsequent "@" as the caret moved right. Portaling to body removes every such ancestor, so
  // `left`/`top` are unconditionally viewport-relative and recomputeAnchor's clamp is correct.
  const popup = (
    <div ref={popupRef} className="a2a-mention-popup" style={{ position: 'fixed', top: anchor?.top ?? 0, left: anchor?.left ?? 0 }}>
      {flat.length === 0 && <div className="a2a-muted" style={{ padding: 8 }}>{t('components.a2a.mentionPopup.noMatches')}</div>}
      {SECTION_ORDER.map((kind) => {
        const items = (grouped[kind] || []).slice(0, MAX_PER_SECTION);
        if (!items.length) return null;
        return (
          <div className="a2a-mention-section" key={kind}>
            <div className="a2a-mention-section-title">{SECTION_LABELS[kind]}</div>
            {items.map((item) => {
              flatIdx++;
              const idx = flatIdx;
              return (
                <div
                  key={`${kind}:${item.token}`}
                  className={`a2a-mention-item ${idx === activeIndex ? 'active' : ''}`}
                  onMouseDown={(e) => { e.preventDefault(); applyMention(item, value, matchStart, query); }}
                  onMouseEnter={() => setActiveIndex(idx)}
                >
                  <span className="a2a-mention-token">{item.token}</span>
                  {item.sublabel && item.sublabel !== item.token && <span className="a2a-muted">{item.sublabel}</span>}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );

  return (
    <>
      {mirror}
      {createPortal(popup, document.body)}
    </>
  );
}
